import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { fetchCompanyQuoteTerms } from "@/lib/quote-terms";
import { PageHeader, EmptyState } from "@/components/app-shell";
import { FileText, Plus, Search, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { formatCurrency, formatDate } from "@/lib/format";
import {
  PROJECT_TYPES, type ProjectType,
  QUOTE_STATUS_LABELS, QUOTE_STATUS_CLASSES, type QuoteStatus,
} from "@/lib/calc";
import { useActiveCompany } from "@/hooks/use-active-company";
import {
  QUOTE_LIST_TABS,
  QUOTE_VIEW_LABELS,
  matchesQuoteView,
  parseQuoteView,
  statusesForView,
  type QuoteView,
} from "@/lib/quote-views";
import { groupQuoteRevisions } from "@/lib/quote-revisions";


export const Route = createFileRoute("/_authenticated/quotes/")({
  validateSearch: (search: Record<string, unknown>): { view?: QuoteView } => {
    const view = parseQuoteView(search.view);
    // «all» er standard og holdes ute av URL-en slik at /quotes forblir ren.
    return view === "all" ? {} : { view };
  },
  head: () => ({
    meta: [
      { title: "Tilbud | VERQO" },
      { name: "description", content: "Tilbud og prosjekter i VERQO." },
    ],
  }),
  component: QuotesPage,
});

const PROJECT_LABEL: Record<string, string> = Object.fromEntries(PROJECT_TYPES.map((p) => [p.value, p.label]));
const STATUS_KEYS = Object.keys(QUOTE_STATUS_LABELS) as QuoteStatus[];

function QuotesPage() {
  const qc = useQueryClient();
  const view: QuoteView = Route.useSearch().view ?? "all";
  const navigate = useNavigate();
  const { activeModuleKey: moduleKey, activeModule: moduleDef } = useActiveCompany();
  const { companyId } = useActiveCompany();


  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [status, setStatus] = useState<string>("");
  const [projectType, setProjectType] = useState<string>("");
  const [sortBy, setSortBy] = useState<"created_desc" | "updated_desc" | "total_desc" | "total_asc">("created_desc");

  const { data: quotes = [], error: quotesError } = useQuery({
    queryKey: ["quotes", companyId, moduleKey],
    enabled: !!companyId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quotes")
        .select("id,quote_number,title,status,project_type,project_number,revision_label,revision_of,total_incl_vat,created_at,updated_at,approved_at,valid_until,sent_at,last_opened_at,opened_count,customers(name)")
        .eq("company_id", companyId)
        .eq("module_key", moduleKey)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });



  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("quotes").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["quotes"] }); toast.success("Tilbud slettet"); },
    onError: (e: Error) => toast.error(e.message),
  });

  const create = useMutation({
    mutationFn: async () => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error("Ikke innlogget");
      if (!companyId) throw new Error("Ingen aktiv bedrift");
      const uid = user.user.id;
      // Hent + oppdater sekvens i settings
      const { data: s } = await supabase.from("settings").select("next_quote_seq,quote_year").eq("user_id", uid).maybeSingle();
      const year = new Date().getFullYear();
      const seq = s?.quote_year === year ? (s?.next_quote_seq ?? 1) : 1;
      const number = `${year}-${String(seq).padStart(3, "0")}`;
      await supabase.from("settings").update({ next_quote_seq: seq + 1, quote_year: year }).eq("user_id", uid);
      // Bolk A4: nye tilbud arver bedriftens standardtekster.
      const terms = await fetchCompanyQuoteTerms(companyId);
      const { data, error } = await supabase.from("quotes").insert({
        user_id: uid,
        company_id: companyId,
        module_key: moduleKey,
        quote_number: number,
        title: "Nytt tilbud",
        status: "draft",
        ...terms,
      }).select().single();


      if (error) throw error;
      try {
        const { logQuoteActivity } = await import("@/lib/quote-activity");
        await logQuoteActivity({
          quoteId: data.id, companyId,
          kind: "created", summary: "Tilbud opprettet",
        });
      } catch { /* stille */ }
      return data;
    },
    onSuccess: (q) => {
      qc.invalidateQueries({ queryKey: ["quotes"] });
      navigate({ to: "/quotes/$id", params: { id: q.id } });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Grupper på faktiske revisjonskoblinger; kun gjeldende revisjon vises/telles.
  const groups = groupQuoteRevisions(quotes);
  const supersededById = new Map(groups.map((g) => [g.current.id, g.superseded]));

  const filtered = groups
    .map((g) => g.current)
    .filter((q) => matchesQuoteView(q as { status?: string | null; approved_at?: string | null; created_at?: string | null }, view))
    .filter((q) => {
      const matchQ = !search || [q.title, q.quote_number, q.project_number, (q.customers as { name: string } | null)?.name].some((v) => v?.toLowerCase().includes(search.toLowerCase()));
      const matchS = !status || q.status === status;
      const matchP = !projectType || (q as { project_type?: string }).project_type === projectType;
      return matchQ && matchS && matchP;
    })
    .sort((a, b) => {
      if (sortBy === "created_desc") return (b.created_at ?? "").localeCompare(a.created_at ?? "");
      if (sortBy === "updated_desc") return ((b as { updated_at?: string }).updated_at ?? "").localeCompare((a as { updated_at?: string }).updated_at ?? "");
      if (sortBy === "total_desc") return Number(b.total_incl_vat ?? 0) - Number(a.total_incl_vat ?? 0);
      if (sortBy === "total_asc") return Number(a.total_incl_vat ?? 0) - Number(b.total_incl_vat ?? 0);
      return 0;
    });

  const currents = groups.map((g) => g.current);
  const tabCounts = Object.fromEntries(
    QUOTE_LIST_TABS.map((t) => [t, currents.filter((q) => matchesQuoteView(q, t)).length]),
  ) as Record<QuoteView, number>;

  return (
    <>
      <PageHeader
        title={`Tilbud — ${moduleDef.label}`}
        subtitle={`${quotes.length} totalt i faget · ${filtered.length} vist`}

        action={
          <button onClick={() => create.mutate()} className="btn btn-primary" disabled={create.isPending}>
            <Plus className="h-4 w-4" /> Nytt tilbud
          </button>
        }
      />

      {/* Faner: samme statusgrupper som dashboardet teller etter. */}
      <div className="mb-3 -mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
        <div className="inline-flex gap-1 rounded-lg bg-muted p-1">
          {QUOTE_LIST_TABS.map((t) => (
            <button
              key={t}
              onClick={() => {
                setStatus("");
                navigate({ to: "/quotes", search: t === "all" ? {} : { view: t } });
              }}
              className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm ${
                view === t ? "bg-background font-medium shadow-sm" : "text-muted-foreground"
              }`}
            >
              {QUOTE_VIEW_LABELS[t]}
              <span className="ml-1.5 text-xs text-muted-foreground">{tabCounts[t]}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-[1fr,auto,auto,auto]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input className="input-base pl-9" placeholder="Søk tittel, kunde, nummer…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select
          className="input-base lg:w-44"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            if (view !== "all") navigate({ to: "/quotes", search: {} });
          }}
        >
          <option value="">Alle statuser</option>
          {STATUS_KEYS.map((k) => <option key={k} value={k}>{QUOTE_STATUS_LABELS[k]}</option>)}
        </select>
        <select className="input-base lg:w-44" value={projectType} onChange={(e) => setProjectType(e.target.value)}>
          <option value="">Alle typer</option>
          {PROJECT_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <select className="input-base lg:w-44" value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)}>
          <option value="created_desc">Nyeste først</option>
          <option value="updated_desc">Sist endret</option>
          <option value="total_desc">Beløp (høy → lav)</option>
          <option value="total_asc">Beløp (lav → høy)</option>
        </select>
      </div>


      {quotesError && (
        <div className="card mb-4 border-destructive/40 p-4 text-sm text-destructive">
          Kunne ikke hente tilbud: {(quotesError as Error).message}
        </div>
      )}

      {view !== "all" && !QUOTE_LIST_TABS.includes(view) && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg bg-muted px-3 py-2 text-sm">
          <span className="text-muted-foreground">Filter:</span>
          <span className="font-medium">{QUOTE_VIEW_LABELS[view]}</span>
          <span className="text-xs text-muted-foreground">
            ({statusesForView(view).map((s) => QUOTE_STATUS_LABELS[s]).join(", ")})
          </span>
          <button
            className="btn btn-ghost ml-auto h-8 px-2 text-xs"
            onClick={() => navigate({ to: "/quotes", search: {} })}
          >
            Vis alle
          </button>
        </div>
      )}

      {quotesError ? null : quotes.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="Ingen tilbud ennå"
          description="Lag ditt første tilbud – kalkyle, materiell og PDF på ett sted."
          action={<button onClick={() => create.mutate()} className="btn btn-primary"><Plus className="h-4 w-4" /> Nytt tilbud</button>}
        />
      ) : (
        <div className="card divide-y divide-border">
          {filtered.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">
              Ingen tilbud i dette utvalget.
            </div>
          )}
          {filtered.map((q) => {
            const st = q.status as QuoteStatus;
            const label = QUOTE_STATUS_LABELS[st] ?? st;
            const cls = QUOTE_STATUS_CLASSES[st] ?? QUOTE_STATUS_CLASSES.draft;
            const pt = (q as { project_type?: ProjectType }).project_type;
            const revLabel = q.revision_label ? ` Rev ${q.revision_label}` : "";
            const numDisplay = q.project_number
              ? `${q.project_number} · ${q.quote_number}${revLabel}`
              : `${q.quote_number}${revLabel}`;
            const history = supersededById.get(q.id) ?? [];
            const open = expanded === q.id;
            return (
              <div key={q.id}>
                <div className="flex items-center gap-3 px-4 py-3">
                  <Link to="/quotes/$id" params={{ id: q.id }} className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold">
                      {(q.customers as { name: string } | null)?.name ?? "Ingen kunde"}
                    </div>
                    <div className="mt-0.5 truncate text-sm">{q.title || "Uten tittel"}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <span className="text-xs font-mono text-muted-foreground">{numDisplay}</span>
                      <span className={`chip ${cls}`}>{label}</span>
                      {history.length > 0 && <span className="chip">Gjeldende revisjon</span>}
                      {pt && pt !== "annet" && <span className="chip">{PROJECT_LABEL[pt] ?? pt}</span>}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      Sist endret{" "}
                      {formatDate((q as { updated_at?: string }).updated_at ?? q.created_at)}
                      {(q as { sent_at?: string }).sent_at && ` · Sendt ${formatDate((q as { sent_at?: string }).sent_at!)}`}
                      {(q as { last_opened_at?: string }).last_opened_at && ` · Åpnet ${formatDate((q as { last_opened_at?: string }).last_opened_at!)}`}
                    </div>
                  </Link>
                  <div className="text-right text-sm font-semibold">{formatCurrency(q.total_incl_vat)}</div>
                  <button onClick={() => { if (confirm("Slette tilbudet?")) del.mutate(q.id); }} className="btn btn-ghost p-2 text-destructive" aria-label="Slett tilbud">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                {history.length > 0 && (
                  <div className="px-4 pb-3">
                    <button
                      className="btn btn-ghost h-8 px-2 text-xs"
                      onClick={() => setExpanded(open ? null : q.id)}
                    >
                      {open ? "Skjul" : "Vis"} tidligere versjoner ({history.length})
                    </button>
                    {open && (
                      <div className="mt-1 space-y-1 border-l border-border pl-3">
                        {history.map((h) => (
                          <Link
                            key={h.id}
                            to="/quotes/$id"
                            params={{ id: h.id }}
                            className="flex items-center justify-between gap-3 rounded-md px-2 py-1.5 text-xs hover:bg-muted"
                          >
                            <span className="font-mono text-muted-foreground">
                              {h.quote_number}
                              {h.revision_label ? ` Rev ${h.revision_label}` : ""}
                            </span>
                            <span className="text-muted-foreground">
                              {QUOTE_STATUS_LABELS[h.status as QuoteStatus] ?? h.status}
                            </span>
                            <span className="font-medium">{formatCurrency(h.total_incl_vat)}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
